import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from './ui/button';
import { 
  Factory, 
  Calculator, 
  ShoppingBag, 
  Briefcase,
  Cog,
  BarChart3,
  Eye,
  Database,
  FileText,
  Shield,
  TrendingUp,
  Users,
  Package,
  Search,
  Heart,
  MessageSquare,
  FileCheck,
  BookOpen,
  Sparkles,
  ArrowRight,
  CheckCircle2
} from 'lucide-react';

// Industry Data
const industriesData = {
  manufacturing: {
    id: 'manufacturing',
    name: 'Manufacturing',
    icon: Factory,
    headline: "Cut Downtime. Automate the Floor. Predict What's Next.",
    subheadline: "Canadian manufacturers are using AI to do more with the same team, and compete globally.",
    outcomeStat: "Up to 40% reduction in unplanned downtime with predictive maintenance.",
    ctaText: "Book a Free Manufacturing AI Call",
    ctaLink: "/contact?industry=manufacturing",
    color: "from-blue-600 to-cyan-500",
    bgColor: "bg-blue-50",
    iconColor: "text-blue-600",
    services: [
      {
        icon: Cog,
        title: "Predictive Maintenance AI",
        description: "Detect equipment failures before they happen. Reduce costly downtime and extend asset life."
      },
      {
        icon: Package,
        title: "Production & Inventory Automation",
        description: "Automate reorder triggers, production scheduling, and supply chain alerts in real time."
      },
      {
        icon: Eye,
        title: "Quality Control & Defect Detection",
        description: "Use computer vision to catch defects on the line, faster and more accurately than manual inspection."
      },
      {
        icon: Database,
        title: "ERP + AI Integration",
        description: "Connect your existing ERP (SAP, Odoo, ERPNext) with AI dashboards for real-time operational insights."
      }
    ],
    problemStatement: "Most manufacturing companies we talk to are dealing with aging equipment, unpredictable maintenance costs, and pressure to increase output without adding headcount."
  },
  accounting: {
    id: 'accounting',
    name: 'Accounting & Finance',
    icon: Calculator,
    headline: "Close Faster. Catch Errors Earlier. Advise Smarter.",
    subheadline: "AI tools built for Canadian accounting firms and finance teams ready to move beyond spreadsheets.",
    outcomeStat: "Save 15+ hours per week on manual reconciliation and reporting.",
    ctaText: "Book a Free Accounting AI Call",
    ctaLink: "/contact?industry=accounting",
    color: "from-emerald-600 to-teal-500",
    bgColor: "bg-emerald-50",
    iconColor: "text-emerald-600",
    services: [
      {
        icon: FileCheck,
        title: "Automated Bookkeeping & Reconciliation",
        description: "AI that categorizes transactions, flags anomalies, and reconciles accounts without manual entry."
      },
      {
        icon: Shield,
        title: "Tax & Compliance Automation",
        description: "Stay ahead of CRA requirements with automated compliance checks and audit-ready reporting."
      },
      {
        icon: TrendingUp,
        title: "AI-Powered Financial Forecasting",
        description: "Predict cash flow, revenue trends, and risk scenarios with live data, not last quarter's numbers."
      },
      {
        icon: BarChart3,
        title: "Client Reporting & Advisory Tools",
        description: "Generate branded client reports automatically and surface insights your clients actually care about."
      }
    ],
    problemStatement: "Most accounting firms we talk to are drowning in manual data entry, struggling to keep up with CRA changes, and spending more time on compliance than advisory."
  },
  retail: {
    id: 'retail',
    name: 'Retail & Ecommerce',
    icon: ShoppingBag,
    headline: "Sell More. Stock Smarter. Keep Customers Coming Back.",
    subheadline: "From Shopify to brick-and-mortar, AI helps Canadian retailers compete with bigger brands on a fraction of the budget.",
    outcomeStat: "Retailers using AI personalization see up to 30% higher average order value.",
    ctaText: "Book a Free Retail AI Call",
    ctaLink: "/contact?industry=retail",
    color: "from-purple-600 to-pink-500",
    bgColor: "bg-purple-50",
    iconColor: "text-purple-600",
    services: [
      {
        icon: Sparkles,
        title: "AI-Powered Product Recommendations",
        description: "Serve the right product to the right customer at the right time, across web, email, and in-store."
      },
      {
        icon: Package,
        title: "Demand Forecasting & Inventory AI",
        description: "Never overstock or run out. Predict what sells, when, and where with machine learning."
      },
      {
        icon: Search,
        title: "AI SEO & Ecommerce Content",
        description: "Automate product descriptions, category pages, and SEO content that ranks and converts."
      },
      {
        icon: Heart,
        title: "Customer Retention & Churn Prediction",
        description: "Identify at-risk customers before they leave and trigger personalized win-back campaigns automatically."
      }
    ],
    problemStatement: "Most retail businesses we talk to are fighting thin margins, dealing with inventory headaches, and watching bigger competitors outspend them on marketing."
  },
  b2b: {
    id: 'b2b',
    name: 'B2B Services',
    icon: Briefcase,
    headline: "Fill Your Pipeline. Shorten Your Sales Cycle. Scale Without Headcount.",
    subheadline: "B2B companies use AI to generate better leads, close faster, and deliver more, without hiring a bigger team.",
    outcomeStat: "B2B teams using AI outreach see 2–3x more qualified meetings booked per month.",
    ctaText: "Book a Free B2B AI Call",
    ctaLink: "/contact?industry=b2b",
    color: "from-orange-600 to-amber-500",
    bgColor: "bg-orange-50",
    iconColor: "text-orange-600",
    services: [
      {
        icon: Users,
        title: "AI Lead Scoring & CRM Automation",
        description: "Automatically score inbound leads, route them to the right rep, and trigger follow-up sequences."
      },
      {
        icon: FileText,
        title: "Proposal & Document Automation",
        description: "Generate tailored proposals, SOWs, and contracts in minutes using AI, not hours."
      },
      {
        icon: BookOpen,
        title: "LLM-Powered Knowledge Bases",
        description: "Build internal AI assistants that answer client and staff questions instantly from your own documentation."
      },
      {
        icon: MessageSquare,
        title: "AI-Driven Content & Thought Leadership",
        description: "Scale your LinkedIn presence, case studies, and SEO content with AI that sounds like your team wrote it."
      }
    ],
    problemStatement: "Most B2B services companies we talk to are stuck in feast-or-famine cycles, spending too much time on proposals that don't close, and struggling to scale without adding overhead."
  }
};

// Homepage Tabbed Industry Section
export const HomepageIndustrySection = () => {
  const [activeTab, setActiveTab] = useState('manufacturing');
  const industries = Object.values(industriesData);
  const activeIndustry = industriesData[activeTab];

  return (
    <section className="section-padding bg-white relative overflow-hidden" data-testid="homepage-industry-section">
      <div className="container-custom">
        {/* Header */}
        <div className="text-center mb-12">
          <motion.span 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-sm font-semibold text-[#2563EB] uppercase tracking-wider"
          >
            Industry Solutions
          </motion.span>
          <motion.h2 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="font-outfit text-3xl md:text-4xl font-bold text-[#0A2540] mt-4 mb-4"
          >
            AI Built for Your Industry
          </motion.h2>
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="text-slate-600 max-w-2xl mx-auto"
          >
            Whether you run a factory floor or a finance firm, we build AI that fits how your business actually works.
          </motion.p>
        </div>

        {/* Tabs */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.3 }}
          className="flex flex-wrap justify-center gap-2 mb-12"
        >
          {industries.map((industry) => (
            <motion.button
              key={industry.id}
              onClick={() => setActiveTab(industry.id)}
              className={`flex items-center gap-2 px-6 py-3 rounded-full font-medium transition-all ${
                activeTab === industry.id
                  ? 'bg-[#0A2540] text-white shadow-lg'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              data-testid={`industry-tab-${industry.id}`}
            >
              <industry.icon className="w-5 h-5" />
              <span className="hidden sm:inline">{industry.name}</span>
            </motion.button>
          ))}
        </motion.div>

        {/* Content */}
        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
            className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start"
          >
            {/* Left - Headline & CTA */}
            <div>
              <div className={`inline-flex items-center gap-2 ${activeIndustry.bgColor} px-4 py-2 rounded-full mb-6`}>
                <activeIndustry.icon className={`w-5 h-5 ${activeIndustry.iconColor}`} />
                <span className={`text-sm font-medium ${activeIndustry.iconColor}`}>{activeIndustry.name}</span>
              </div>
              
              <h3 className="font-outfit text-2xl md:text-3xl font-bold text-[#0A2540] mb-4">
                {activeIndustry.headline}
              </h3>
              
              <p className="text-slate-600 mb-6">
                {activeIndustry.subheadline}
              </p>

              {/* Outcome Stat */}
              <div className={`bg-gradient-to-r ${activeIndustry.color} p-6 rounded-2xl mb-8`}>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                    <TrendingUp className="w-6 h-6 text-white" />
                  </div>
                  <p className="text-white font-medium">
                    {activeIndustry.outcomeStat}
                  </p>
                </div>
              </div>

              <Link to={activeIndustry.ctaLink}>
                <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                  <Button className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white rounded-full px-8 py-6 text-lg font-medium shadow-lg">
                    {activeIndustry.ctaText}
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </motion.div>
              </Link>
            </div>

            {/* Right - Service Cards Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {activeIndustry.services.map((service, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white p-6 rounded-xl border border-slate-100 shadow-sm hover:shadow-lg hover:border-slate-200 transition-all group"
                  data-testid={`industry-service-${index}`}
                >
                  <motion.div 
                    className={`w-12 h-12 bg-gradient-to-br ${activeIndustry.color} rounded-xl flex items-center justify-center mb-4`}
                    whileHover={{ scale: 1.1, rotate: 5 }}
                  >
                    <service.icon className="w-6 h-6 text-white" />
                  </motion.div>
                  <h4 className="font-outfit font-semibold text-[#0A2540] mb-2 group-hover:text-[#2563EB] transition-colors">
                    {service.title}
                  </h4>
                  <p className="text-sm text-slate-600">
                    {service.description}
                  </p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
};

// AI Solutions Page Industry Section
export const AISolutionsIndustrySection = () => {
  const industries = Object.values(industriesData);

  return (
    <section className="section-padding bg-slate-50" data-testid="ai-solutions-industry-section">
      <div className="container-custom">
        {/* Header */}
        <div className="text-center mb-16">
          <motion.span 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-sm font-semibold text-[#2563EB] uppercase tracking-wider"
          >
            Industry Expertise
          </motion.span>
          <motion.h2 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="font-outfit text-3xl md:text-4xl font-bold text-[#0A2540] mt-4 mb-4"
          >
            We Speak Your Industry's Language
          </motion.h2>
        </div>

        {/* Industry Blocks */}
        <div className="space-y-8">
          {industries.map((industry, index) => (
            <motion.div
              key={industry.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="bg-white rounded-2xl border border-slate-100 overflow-hidden hover:shadow-xl transition-shadow"
              data-testid={`industry-block-${industry.id}`}
            >
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-0">
                {/* Left - Industry Info */}
                <div className={`p-8 bg-gradient-to-br ${industry.color}`}>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center">
                      <industry.icon className="w-7 h-7 text-white" />
                    </div>
                    <h3 className="font-outfit text-2xl font-bold text-white">{industry.name}</h3>
                  </div>
                  
                  <p className="text-white/80 mb-6">
                    {industry.problemStatement}
                  </p>

                  <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 mb-6">
                    <p className="text-white text-sm font-medium">{industry.outcomeStat}</p>
                  </div>

                  <Link to={`/ai-solutions/${industry.id}`}>
                    <motion.div whileHover={{ x: 5 }}>
                      <span className="text-white font-medium flex items-center gap-2">
                        Explore AI for {industry.name}
                        <ArrowRight className="w-4 h-4" />
                      </span>
                    </motion.div>
                  </Link>
                </div>

                {/* Right - Services */}
                <div className="lg:col-span-2 p-8">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {industry.services.map((service, sIndex) => (
                      <div key={sIndex} className="flex items-start gap-3">
                        <div className={`w-10 h-10 ${industry.bgColor} rounded-lg flex items-center justify-center flex-shrink-0`}>
                          <service.icon className={`w-5 h-5 ${industry.iconColor}`} />
                        </div>
                        <div>
                          <h4 className="font-semibold text-[#0A2540] mb-1">{service.title}</h4>
                          <p className="text-sm text-slate-600">{service.description}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export { industriesData };
export default HomepageIndustrySection;
